import { useMemo, useEffect } from 'react';
import { Formik, useFormikContext } from 'formik';

import { useAlert } from 'hooks';
import { Row, Col } from 'components/grid';
import * as CommonStyles from 'components/styles';
import { Dialog, DialogType } from 'components/dialog';
import { Button, ButtonTypes } from 'components/button';
import { InputField, SelectField } from 'components/fields';
import { formatPrice, isSingleSelectValue } from 'utils/helpers';
import { ButtonColors, PaymentMethodType, RefundPaymentMethod } from 'types';

import { FormValues as CommonFormValues } from '../types';
import {
  calculateMaxRefundAmounts,
  calculateAvailableRefundAmounts,
} from '../helpers';

import * as Styles from './styles';
import { FormProps } from './types';
import {
  getInitialValues,
  getValidationSchema,
  getPaymentTypeOptions,
} from './config';

export const Form = ({
  data,
  isEdit,
  trigger,
  onSubmit,
  checkout,
  maxAllowedTipRefund,
  allowedPaymentMethodTypes,
  maxAllowedFreeShippingRefund,
  maxAllowedProcessingFeeRefund,
}: FormProps) => {
  const { showErrorAlert } = useAlert();
  const { values: commonValues } = useFormikContext<CommonFormValues>();

  const { maxAllowedRefund } = calculateMaxRefundAmounts(checkout);

  const {
    remainingAmount,
    remainingTipAmount,
    selectedItemsAmount,
    remainingFreeShippingAmount,
    remainingProcessingFeeAmount,
  } = calculateAvailableRefundAmounts({
    data: checkout,
    maxAllowedRefund,
    maxAllowedTipRefund,
    items: commonValues.items,
    maxAllowedFreeShippingRefund,
    maxAllowedProcessingFeeRefund,
    excludeId: isEdit ? data?.id : undefined,
    returnPayments: commonValues.returnPayments,
  });

  const paymentTypeOptions = useMemo(
    () => getPaymentTypeOptions(allowedPaymentMethodTypes),
    [allowedPaymentMethodTypes]
  );

  const isPaymentBalanceEmpty =
    remainingAmount <= 0 &&
    remainingTipAmount <= 0 &&
    remainingProcessingFeeAmount <= 0 &&
    remainingFreeShippingAmount <= 0;

  return (
    <Dialog trigger={trigger} title={`${isEdit ? 'Edit' : 'Add'} Payment`}>
      {(close, _, ButtonWrap) => (
        <Formik
          onSubmit={async (values) => {
            await onSubmit(values);
            close();
          }}
          initialValues={getInitialValues({
            data,
            amount: remainingAmount,
            tipAmount: remainingTipAmount,
            freeShippingAmount: remainingFreeShippingAmount,
            processingFeeAmount: remainingProcessingFeeAmount,
          })}
          validationSchema={getValidationSchema({
            maxAmount: remainingAmount,
            maxTipAmount: remainingTipAmount,
            maxFreeShippingAmount: remainingFreeShippingAmount,
            maxProcessingFeeAmount: remainingProcessingFeeAmount,
            isManualCreditPaymentEnabled:
              checkout?.isManualCreditPaymentEnabled,
          })}
        >
          {({ dirty, values, isSubmitting, setFieldValue }) => {
            const isShowCloverCreditCardSelect =
              values.type === RefundPaymentMethod.Credit &&
              !checkout?.isManualCreditPaymentEnabled;

            const selectedCard = checkout?.refundedByCloverCards?.find(
              (card) => card.paymentId === values.cloverPaymentId
            );

            useEffect(() => {
              if (isPaymentBalanceEmpty) {
                showErrorAlert(
                  'The balance of the final refund and tip is empty, you cannot add another payment method'
                );
                close();
              }
            }, []);

            return (
              <CommonStyles.Form>
                <Styles.AvailableAmountContainer>
                  <Styles.AvailableAmount>
                    <span>Available Amount:</span>{' '}
                    {formatPrice(remainingAmount)}
                  </Styles.AvailableAmount>

                  {values.type === RefundPaymentMethod.Credit &&
                    values.cloverPaymentId &&
                    selectedCard && (
                      <>
                        <Styles.AvailableAmount>
                          <span>Card Available:</span>{' '}
                          {formatPrice(selectedCard.availableForRefund)}
                        </Styles.AvailableAmount>

                        <Styles.AvailableAmount>
                          <span>Card Fee Available:</span>{' '}
                          {formatPrice(selectedCard.availableFeeRefund)}
                        </Styles.AvailableAmount>
                      </>
                    )}

                  {!!maxAllowedTipRefund && (
                    <Styles.AvailableAmount>
                      <span>Available Tip:</span>{' '}
                      {formatPrice(remainingTipAmount)}
                    </Styles.AvailableAmount>
                  )}

                  {!!maxAllowedProcessingFeeRefund && (
                    <Styles.AvailableAmount>
                      <span>Available Processing Fee:</span>{' '}
                      {formatPrice(maxAllowedProcessingFeeRefund)}
                    </Styles.AvailableAmount>
                  )}

                  {!!maxAllowedFreeShippingRefund && (
                    <Styles.AvailableAmount>
                      <span>Available Free Shipping:</span>{' '}
                      {formatPrice(remainingFreeShippingAmount)}
                    </Styles.AvailableAmount>
                  )}

                  <Styles.AvailableAmount>
                    <span>Selected Products Amount:</span>{' '}
                    {formatPrice(selectedItemsAmount)}
                  </Styles.AvailableAmount>
                </Styles.AvailableAmountContainer>

                <Row>
                  <Col>
                    <InputField
                      number
                      decimal
                      name="amount"
                      label="Amount"
                      placeholder="Amount"
                    />
                  </Col>
                </Row>

                {!!maxAllowedTipRefund && (
                  <Row>
                    <Col>
                      <InputField
                        number
                        decimal
                        name="tip"
                        label="Tip Amount"
                        placeholder="Tip Amount"
                      />
                    </Col>
                  </Row>
                )}

                {!!maxAllowedProcessingFeeRefund && (
                  <Row>
                    <Col>
                      <InputField
                        number
                        decimal
                        name="processingFee"
                        label="Processing Fee Amount"
                        placeholder="Processing Fee Amount"
                      />
                    </Col>
                  </Row>
                )}

                {!!maxAllowedFreeShippingRefund && (
                  <Row>
                    <Col>
                      <InputField
                        number
                        decimal
                        name="freeShipping"
                        label="Free Shipping Amount"
                        placeholder="Free Shipping Amount"
                      />
                    </Col>
                  </Row>
                )}

                <Row>
                  <Col>
                    <SelectField
                      onlyValue
                      name="type"
                      label="Type"
                      placeholder="Select Type"
                      options={paymentTypeOptions}
                      onChange={(e) => {
                        if (
                          isSingleSelectValue(e) &&
                          e?.value === PaymentMethodType.Cash
                        ) {
                          setFieldValue('cloverPaymentId', '');
                        }
                      }}
                    />
                  </Col>
                </Row>

                {isShowCloverCreditCardSelect && (
                  <Row>
                    <Col>
                      <SelectField
                        onlyValue
                        label="Credit Card"
                        name="cloverPaymentId"
                        placeholder="Select Credit Card"
                        options={
                          checkout?.refundedByCloverCards
                            ?.filter(
                              (card) =>
                                card.availableForRefund ||
                                card.availableTipRefund
                            )
                            ?.map(
                              ({
                                last4,
                                paymentId,
                                availableForRefund,
                                availableTipRefund,
                              }) => ({
                                value: paymentId,
                                label: `**** ${last4} (${formatPrice(availableForRefund || availableTipRefund)})`,
                              })
                            ) || []
                        }
                      />
                    </Col>
                  </Row>
                )}

                <ButtonWrap type={DialogType.Plain}>
                  <Button
                    text="Cancel"
                    onClick={close}
                    colorType={ButtonColors.LightBlue}
                  />
                  <Button
                    text="Add"
                    type={ButtonTypes.Submit}
                    disabled={!dirty || isSubmitting}
                  />
                </ButtonWrap>
              </CommonStyles.Form>
            );
          }}
        </Formik>
      )}
    </Dialog>
  );
};
